import { LoanParams } from 'app/models/loan-params'
import { Modal } from '@mantine/core'
import { useDispatch, useSelector } from 'react-redux'
import { getPrincipalPayments } from 'app/state/ducks/principal-payment/selectors'
import { loadAllPrincipalPayments } from 'app/state/ducks/principal-payment/thunks'
import { useMemo, useState } from 'react'
import { PrincipalPaymentScreen } from './principal-payment-stepper'
import { getFacilities, getFacility } from 'app/state/ducks/facilities/selectors'
import { loadOneFacility } from 'app/state/ducks/facilities/thunks'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { FacilityParams } from 'app/models/facility-params'
import { PrincipalPaymentParams } from 'app/models/principal-payments-params'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import { CenteredLoader } from 'app/views/components/loader/loader'
import { usePrerequisiteReads } from 'app/hooks/usePrerequisiteReads'

type Props = {
  open: boolean
  setOpen: any
  loan: LoanParams
}

export default function PrincipalPaymentModal({ loan, open, setOpen }: Props) {
  const dispatch = useDispatch()
  const principalPayments = useSelector(getPrincipalPayments)
  const facilities = useSelector(getFacilities)
  const loadedFacility = useSelector(getFacility)
  const entities = useSelector(getEntities)


  // The modal reads the loan's open payment, its facility and the entities
  // from Redux; load them here so it works wherever it is opened from (the
  // Servicing and My Work pages load none of them up front). The facility is
  // fetched alone unless the page already holds the list; the entities — the
  // lender names in LenderBreakdown and ECFDecision — only when the store
  // is cold. Appended last: `onLoaded` reads the facility at index 1.
  // The screen opens once both have settled: before that the loan's open
  // payment is unknown and a Save would create a second one. The modal stays
  // mounted while closed, so the reads run again on each opening.
  const ready = usePrerequisiteReads(
    () => [
      dispatch(loadAllPrincipalPayments()),
      ...(facilities.some(f => f.id === loan.facilityId.id)
        ? []
        : [dispatch(loadOneFacility(loan.facilityId.id, 'Approved'))]),
      ...(entities?.length ? [] : [dispatch(loadEntities())]),
    ],
    () => {
      ErrorNotification({
        title: 'Could not open',
        message: "The loan's payments, its facility or the entities could not be loaded. Please try again.",
      })
      setOpen(false)
    },
    {
      // and again for another loan on the same mounted modal
      deps: [open, loan.id, loan.facilityId.id],
      enabled: open,
      onLoaded: results => {
        // the facility read, when one was issued, is the second result; kept
        // here because the store's single-facility slot is shared and a
        // request from a form closed earlier can overwrite it later
        const read = (results[1] as { payload?: FacilityParams } | undefined)?.payload
        if (read?.id === loan.facilityId.id) setOwnFacility(read)
      },
    },
  )
  const [ownFacility, setOwnFacility] = useState<FacilityParams | undefined>()

  // Derived from the CURRENT loan and the store on every render — never
  // copied into state by an effect that forgot the loan, which handed a new
  // loan its predecessor's facility and payment.
  const payment = useMemo<PrincipalPaymentParams | undefined>(
    () =>
      principalPayments?.find(
        candidate => candidate.loanId.id == loan.id && candidate.status != 'Approved',
      ),
    [principalPayments, loan.id],
  )
  const facility = useMemo<FacilityParams | undefined>(() => {
    const fromList = facilities?.find(f => f.id == loan.facilityId.id)
    const fromOwnRead = ownFacility?.id == loan.facilityId.id ? ownFacility : undefined
    const fromSlot = loadedFacility?.id == loan.facilityId.id ? loadedFacility : undefined
    return fromList ?? fromOwnRead ?? fromSlot
  }, [facilities, ownFacility, loadedFacility, loan.facilityId.id])

  return (
    <Modal
      size="70%"
      className="modal-body modal-body-template fit-content-modal"
      opened={open}
      onClose={() => setOpen(false)}
    >
      {ready && facility ? (
        <PrincipalPaymentScreen loan={loan} payment={payment} onClose={() => setOpen(false)} facility={facility} />
      ) : (
        <CenteredLoader />
      )}
    </Modal>
  )
}
